import { JsonObject } from '@eagle/common';
import { AccountType, CreateAccountFromTemplateRequest, SharedThing, ThingType } from '@eagle/core-data-types';
import { Card, CardContent, Stack, styled, Typography } from '@ethings/ui-toolkit';
import { Grid, useMediaQuery, useTheme } from '@mui/material';
import { isEmpty, isString } from 'lodash';
import { ChangeEvent, FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthenticated } from '../../../auth';
import { EntityTypes, useEntityDisplay, useFetchAllCache, usePromise, useResponsiveText } from '../../../hooks';
import { formatProperty } from '../../../operators';
import { CacheDataTypes } from '../../../types';
import { innerText, isValueObject } from '../../../util';
import { ErrorMessage } from '../../error-message';
import { FormatProperty } from '../../format/format-property';
import { MiddleSpinner } from '../../middle-spinner';
import { SelectionCheckBox } from '../../selection-check-box';
import { StakeHolderAccountCard, StakeholderConfirmCard } from '../../stakeholder-card';
import { DataRow } from '../../static-data';
import { useStageConfirmDialogContext } from './stage-confirm-dialog-context';
import { StakeholderCardBase } from './stakeholder-card-base';

interface DetailRowProps {
  propertyKey: string;
  label: string;
  sharedThing: SharedThing;
}

const StyledCard = styled(Card)({
  backgroundColor: '#f5f5f5',
});

const DetailRow: FC<DetailRowProps> = ({ propertyKey, label, sharedThing }) => {
  const { t } = useTranslation(['common']);
  const isReference = ['brand', 'country', 'model', 'variant'].includes(propertyKey);
  const rawValue = sharedThing.properties[propertyKey] as JsonObject | string | undefined;
  const entityId = isValueObject(rawValue) ? String(rawValue.value) : String(rawValue);

  const { display, loading } = useEntityDisplay(
    isReference ? (propertyKey as EntityTypes) : 'brand',
    isReference ? entityId : undefined,
  );

  const value = isReference
    ? (loading ? t('common:common.labels.loading') : display)
    : entityId;

  const displayValue = !value || isEmpty(value)
    ? t('common:component.shared-thing-card.hint.no-value')
    : value;

  return (
    <DataRow
      key={propertyKey}
      label={label}
      value={displayValue}
      data-testid={`property-display-${propertyKey}`}
    />
  );
};

const referencePropertyKeys = ['variant', 'model', 'country'] as const;

const VehicleDetailsSection: FC<{ sharedThing: SharedThing }> = ({ sharedThing }) => {
  const { t } = useTranslation(['common']);

  const titleText = t('common:page.thing-detail.title');
  const h6Styles = useResponsiveText('1.25rem', innerText(titleText).length, {
    minFontSize: '1rem',
    maxFontSize: '1.25rem',
    textLengthThreshold: 20,
  });

  const vehicleProperties = Object.entries(sharedThing.properties).map(
    ([key]) => {
      const result: { key: string; label: string; isReference?: boolean } = {
        key,
        label: key.charAt(0).toUpperCase() + key.slice(1),
      };
      if (referencePropertyKeys.includes(key as typeof referencePropertyKeys[number])) {
        result.isReference = true;
      }
      return result;
    },
  );

  return (
    <Stack spacing={1}>
      <Typography
        variant="h6"
        style={{
          fontSize: h6Styles.fontSize,
          lineHeight: h6Styles.lineHeight,
          wordBreak: h6Styles.wordBreak,
          whiteSpace: h6Styles.whiteSpace,
          overflow: h6Styles.overflow,
          textOverflow: h6Styles.textOverflow,
        }}
      >
        {titleText}
      </Typography>
      <StyledCard>
        <CardContent style={{ padding: '8px' }}>
          <Stack spacing={1}>
            {vehicleProperties.map(({ key, label }) => (
              <DetailRow
                key={key}
                propertyKey={key}
                label={label}
                sharedThing={sharedThing}
              />
            ))}
          </Stack>
        </CardContent>
      </StyledCard>
    </Stack>
  );
};

const StageDetailsConfirmSection: FC = () => {
  const { state, stage } = useStageConfirmDialogContext();
  const { t } = useTranslation(['common', 'admin']);

  const titleText = t('admin:page.thing-detail.update-stage-dialog.stage-details.labels');
  const h6Styles = useResponsiveText('1.25rem', innerText(titleText).length, {
    minFontSize: '1rem',
    maxFontSize: '1.25rem',
    textLengthThreshold: 20,
  });

  if (!state.stageProperties || !stage.stageProperties || Object.keys(stage.stageProperties).length === 0) {
    return null;
  }

  const availableScopes = Object.keys(stage.stageProperties).filter(
    (scope) => scope in (state.stageProperties || {}) && state.addOns?.[scope],
  );

  if (availableScopes.length === 0) {
    return null;
  }

  const getPropertyLabel = (scope: string, propertyName: string): string => {
    const definition = stage.stageProperties?.[scope]?.definitions?.[propertyName];
    if (definition?.label) {
      return definition.label;
    }

    const translationKey = `common:common.labels.${propertyName}`;
    const defaultLabel = propertyName.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase());
    return t(translationKey, { defaultValue: defaultLabel });
  };

  return (
    <Stack spacing={1}>
      <Typography
        variant="h6"
        style={{
          fontSize: h6Styles.fontSize,
          lineHeight: h6Styles.lineHeight,
          wordBreak: h6Styles.wordBreak,
          whiteSpace: h6Styles.whiteSpace,
          overflow: h6Styles.overflow,
          textOverflow: h6Styles.textOverflow,
        }}
      >
        {titleText}
      </Typography>
      {availableScopes.map((scope) => {
        const properties = state.stageProperties?.[scope] || {};
        const stageProperties = stage.stageProperties?.[scope];
        const requiredProperties = stageProperties?.requiredProperties || [];
        const propertiesOrder = stageProperties?.order || [];

        const propertiesToDisplay = Array.from(new Set([
          ...Object.keys(properties),
          ...requiredProperties,
        ])).filter((prop) => propertiesOrder.includes(prop));

        if (propertiesToDisplay.length === 0) {
          return null;
        }

        return (
          <StyledCard key={scope}>
            <CardContent style={{ padding: '8px' }}>
              <Stack spacing={1}>
                {propertiesToDisplay.map((propertyName) => {
                  const value = properties[propertyName]?.value ?? '';
                  const label = getPropertyLabel(scope, propertyName);
                  const isRequired = requiredProperties.includes(propertyName);
                  const hasValue = Boolean(value);
                  const definition = stageProperties?.definitions?.[propertyName];

                  if (!hasValue && !isRequired) {
                    return null;
                  }

                  const formattedValue = definition && isString(value) ? formatProperty({ value, definition }) : String(value);

                  return (
                    <DataRow
                      key={`${scope}.${propertyName}`}
                      label={label}
                      value={formattedValue}
                      data-testid={`stage-property-${scope}-${propertyName}`}
                    />
                  );
                })}
              </Stack>
            </CardContent>
          </StyledCard>
        );
      })}
    </Stack>
  );
};

const ThingDetailsConfirmSection: FC = () => {
  const { state, thing } = useStageConfirmDialogContext();
  const { t } = useTranslation(['common', 'admin']);
  const thingTypeCache = useFetchAllCache(CacheDataTypes.THING_TYPE);

  const titleText = t('admin:page.thing-detail.update-stage-dialog.thing-details.labels');
  const h6Styles = useResponsiveText('1.25rem', innerText(titleText).length, {
    minFontSize: '1rem',
    maxFontSize: '1.25rem',
    textLengthThreshold: 20,
  });

  const [thingType, thingTypeError, thingTypeState] = usePromise(async () => {
    if (!thing?.thingTypeId) return null;
    return thingTypeCache.one<ThingType>(thing.thingTypeId);
  }, [thing?.thingTypeId, thingTypeCache]);

  if (!state.requiredProperties || Object.keys(state.requiredProperties).length === 0) {
    return null;
  }

  if (thingTypeError || thingTypeState === 'pending' || !thingType) {
    return null;
  }

  return (
    <Stack spacing={1}>
      <Typography
        variant="h6"
        style={{
          fontSize: h6Styles.fontSize,
          lineHeight: h6Styles.lineHeight,
          wordBreak: h6Styles.wordBreak,
          whiteSpace: h6Styles.whiteSpace,
          overflow: h6Styles.overflow,
          textOverflow: h6Styles.textOverflow,
        }}
      >
        {titleText}
      </Typography>
      <StyledCard>
        <CardContent style={{ padding: '8px' }}>
          <Stack spacing={1}>
            {Object.entries(state.requiredProperties).map(([key, value]) => {
              const propDefinition = thingType.properties.definitions[key] ||
                thingType.sharedProperties?.definitions[key] || {
                type: 'text',
                label: key.charAt(0).toUpperCase() + key.slice(1),
              };

              const displayLabel = propDefinition.label ||
                t(`admin:page.thing-detail.update-stage-dialog.${key}.labels`,
                  { defaultValue: key.charAt(0).toUpperCase() + key.slice(1) });

              return (
                <DataRow
                  key={key}
                  label={displayLabel}
                  value={<FormatProperty definition={propDefinition} value={value} />}
                  data-testid={`required-property-${key}`}
                />
              );
            })}
          </Stack>
        </CardContent>
      </StyledCard>
    </Stack>
  );
};

export const StageConfirmDialogConfirmStep: FC = () => {
  const { state, stage, thing, dispatch, lifecycleTemplate, sharedThingId } = useStageConfirmDialogContext();
  const { t } = useTranslation(['common', 'admin']);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const sortedStakeholders = useMemo(() => {
    if (state.config?.skipStakeholderSelection) return [];

    const targetStage = lifecycleTemplate.stages.find((s) => s.stageId === stage.stageId);
    const requiredStakeholders = targetStage?.enterConditions?.requiredStakeholders || [];

    return state.stakeholders
      .filter((stakeholder) => {
        if (state.config?.isSelfClaim) {
          return (stakeholder.accountId && !stakeholder.initialAccountId) || stakeholder.isCreatingNewAccount;
        }
        return requiredStakeholders.includes(stakeholder.role) || stakeholder.accountId || stakeholder.isCreatingNewAccount;
      })
      .sort((a, b) => {
        // Sort by required first, then by those with accountId or creating new account
        if (a.isRequired !== b.isRequired) return a.isRequired ? -1 : 1;
        if (a.isCreatingNewAccount !== b.isCreatingNewAccount) return a.isCreatingNewAccount ? -1 : 1;
        if (Boolean(a.accountId) !== Boolean(b.accountId)) return a.accountId ? -1 : 1;
        return 0;
      });
  }, [state.stakeholders, state.config, lifecycleTemplate, stage.stageId]);

  const handleClick = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    dispatch({ type: 'SET_CONFIRMATION', value: e.target.checked });
  }, [dispatch]);

  const confirmationText = state.config?.isSelfClaim
    ? t('common:component.self-claim.confirm-text', {
      stageDisplay: stage.display,
      thingDisplay: thing?.display,
    })
    : t('admin:page.thing-detail.update-stage.selection-confirmed.labels');

  const updateStageText = t('admin:page.thing-detail.update-stage.confirm-selection.labels', {
    thingDisplay: thing?.display,
    stageName: stage.display,
  });

  const hasStageProperties = Boolean(state.stageProperties);
  const hasRequiredProperties = Boolean(state.requiredProperties && Object.keys(state.requiredProperties).length > 0);
  const hasThingDetails = Boolean(state.config?.isSelfClaim && state.config.selectedSharedThing);
  const useTwoColumnLayout = (hasStageProperties || hasRequiredProperties || hasThingDetails) && !isMobile && !state.config?.isSelfClaim;

  const stakeholdersContent = (
    <Stack spacing={2} mb={2}>
      {(!state.config?.isSelfClaim || !state.config?.skipStakeholderSelection) && (
        <Stack spacing={2}>
          {sortedStakeholders.map((stakeholder) => (
            stakeholder.isCreatingNewAccount && stakeholder.createAccount ? (
              <StakeholderItem
                key={stakeholder.role}
                stakeholderData={stakeholder.createAccount.data}
                stakeholderRole={stakeholder.role}
              />
            ) : stakeholder.accountId ? (
              <StakeholderConfirmCard
                key={stakeholder.role}
                hideRole={state.isSimplifiedAccountSelect}
                role={stakeholder.role}
                sharedThingId={sharedThingId}
                accountId={stakeholder.accountId}
              />
            ) : null
          ))}
        </Stack>
      )}

    </Stack>
  );

  const propertiesContent = (
    <Stack spacing={2}>
      {hasStageProperties && <StageDetailsConfirmSection />}
      {hasRequiredProperties && <ThingDetailsConfirmSection />}
      {hasThingDetails && state.config?.selectedSharedThing && (
        <VehicleDetailsSection sharedThing={state.config.selectedSharedThing} />
      )}
    </Stack>
  );

  const updateStageConfirmation = <>
    {!state.config?.isSelfClaim && (
      <>
        {state.renderConfirmation ? state.renderConfirmation(state) : (
          <Typography>
            {updateStageText}
          </Typography>
        )}
      </>
    )}
    <SelectionCheckBox
      checked={state.confirmationChecked}
      handleClick={handleClick}
      label={confirmationText}
      data-testid="stage-dialog-confirm-checkbox"
      sx={{ pl: 0 }}
    />
  </>;

  return (
    <Stack spacing={2}>
      {useTwoColumnLayout ? (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            {stakeholdersContent}
            {updateStageConfirmation}
          </Grid>
          <Grid item xs={12} md={6}>
            {propertiesContent}
          </Grid>
        </Grid>
      ) : (
        <>
          {stakeholdersContent}
          {propertiesContent}
          {updateStageConfirmation}
        </>
      )}

    </Stack>
  );
};

interface StakeholderItemProps {
  stakeholderData: CreateAccountFromTemplateRequest;
  stakeholderRole: string;
}

const StakeholderItem: FC<StakeholderItemProps> = ({ stakeholderData, stakeholderRole }) => {
  const { t } = useTranslation(['common', 'admin']);
  const accountTypesCache = useFetchAllCache(CacheDataTypes.ACCOUNT_TYPE);
  const { accountType: loggedInUserAccountType } = useAuthenticated();
  const [accountType, accountTypeError, accountTypeStatus] = usePromise(
    () => accountTypesCache.one<AccountType>(stakeholderData.accountTypeId),
    [accountTypesCache, stakeholderData.accountTypeId],
  );

  if (accountTypeStatus === 'pending') return <MiddleSpinner />;
  if (accountTypeError) return <ErrorMessage error={accountTypeError} />;

  return (
    <StakeholderCardBase role={stakeholderRole}>
      <Stack spacing={1}>
        <StakeHolderAccountCard
          accountType={accountType}
          display={stakeholderData.display}
          properties={stakeholderData.properties}
          role={stakeholderRole}
          initialUsers={stakeholderData.users}
        />
        {loggedInUserAccountType?.display === 'Manufacturer' && (
          <Typography variant="caption" color="textSecondary">
            {t('admin:page.thing-detail-update-stage-dialog.account-request.hint')}
          </Typography>
        )}
      </Stack>
    </StakeholderCardBase>
  );
};
